import {
  type Dispatch,
  memo,
  type SetStateAction,
  useCallback,
  useMemo
} from 'react';

import Path from 'path-to-regexp';
import { useHistory, useLocation, useParams } from 'react-router';

import { Button } from '@/shadcn/button';

import { Footer } from '@/components/layouts';
import { DataEditor } from '@/components/ui/dataEditor/dataEditor';
import { DataImporter } from '@/components/ui/dataImporter/dataImporter';
import { Tray } from '@/components/ui/tray/tray';
import { Widget } from '@/components/ui/widget';
import { TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';
import {
  type CustomLocation,
  type CustomParams,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import type { TraceAction, WorkflowContextType } from '@/types/workflow.types';
import { useToggle } from '@/utils/hooks';
import type { SetFunctionAsState } from '@/utils/hooks/useFunctionAsState';
import { WorkflowContext } from '@/utils/workflowContext';

type Props = {
  action: TraceAction;
  workflowContext: WorkflowContextType;
  traces: any[];
  toggleSignDialog: () => void;
  disabled: boolean;
  setFormData: Dispatch<SetStateAction<Record<string, unknown>>>;
  setCleanupFn: SetFunctionAsState;
};

export const DataTool = memo(
  ({
    action,
    workflowContext,
    traces,
    toggleSignDialog,
    disabled,
    setFormData,
    setCleanupFn
  }: Props) => {
    const location = useLocation<CustomLocation>();
    const history = useHistory<CustomLocation>();
    const params = useParams<CustomParams>();

    const [showTraceInfoTray, toggleShowTraceInfoTray] = useToggle(false);

    const goBack = useCallback(() => {
      if (location.state && location.state.from) {
        history.push(location.state.from);
        return;
      }
      // by default go back to workflow overview
      history.push(Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: params.wfid }));
    }, [params, location]);

    const handleDataToolSubmit = useCallback(
      (data, fn) => {
        setFormData(data);
        setCleanupFn(fn);
        toggleSignDialog();
      },
      [setFormData, setCleanupFn, toggleSignDialog]
    );

    const { rowId: workflowRowId } = workflowContext;
    const dataToolCachingKey = useMemo(
      () =>
        action.dataEditor
          ? `dataEditor_${workflowRowId}_${location.search}`
          : `dataImporter_${workflowRowId}_${location.search}`,
      [action, workflowRowId, location]
    );

    if (traces.length !== 1) {
      throw new Error(
        [
          'Data tool actions are only available for a single trace.',
          `${action.title || 'this action'} is not allowed for ${
            traces.length === 0 ? 'trace initialization' : 'batch update'
          }.`
        ].join('\n')
      );
    }

    const [trace] = traces;
    const traceInfoViewer = useMemo(
      () =>
        trace.workflow.config.info
          ? {
              showTraceInfoTray,
              toggleShowTraceInfoTray
            }
          : undefined,
      [showTraceInfoTray, trace]
    );

    return (
      <WorkflowContext.Provider value={workflowContext}>
        <div className="relative top-subnavbar flex h-[calc(100vh-var(--spacing-full-navbar)-var(--workspace-frame-headroom,0px))] w-full flex-col flex-nowrap justify-between bg-background">
          {action.dataEditor ? (
            <DataEditor
              cachedUpdatesKey={dataToolCachingKey}
              config={action.dataEditor}
              data={trace.state}
              onCancel={goBack}
              onSubmit={handleDataToolSubmit}
              submitDisabled={disabled}
              traceInfoViewer={traceInfoViewer}
            />
          ) : (
            <DataImporter
              // @ts-expect-error DataImporter is not typed yet
              cachedUpdatesKey={dataToolCachingKey}
              config={action.dataImporter}
              onCancel={goBack}
              onSubmit={handleDataToolSubmit}
              submitDisabled={disabled}
              traceInfoViewer={traceInfoViewer}
            />
          )}
        </div>
        {trace.workflow.config.info && showTraceInfoTray && (
          <Tray
            minWidth={450}
            onClose={toggleShowTraceInfoTray}
            portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
            title="**Trace info** viewer"
          >
            <div className="flex h-full flex-col flex-nowrap text-foreground text-sm">
              <div className="grow overflow-auto">
                <div className="p-2.5">
                  <Widget
                    // @ts-expect-error Widget is not typed yet
                    data={trace.state}
                    links={trace?.links?.nodes}
                    widget={trace.workflow.config.info}
                  />
                </div>
              </div>
              <div className="flex h-18 w-full shrink-0 flex-row flex-nowrap items-center justify-between p-3.5">
                <div className="flex flex-row flex-nowrap items-center justify-start">
                  <Button
                    aria-label="Close the Trace info panel"
                    data-cy="trace-info-tray-close-button"
                    onClick={toggleShowTraceInfoTray}
                    type="button"
                    variant="outline"
                  >
                    CLOSE
                  </Button>
                </div>
              </div>
            </div>
          </Tray>
        )}
        <Footer />
      </WorkflowContext.Provider>
    );
  }
);
